import { v4 as getUUID } from 'uuid';
import { SessionControllerConfiguration, SessionEvents, SessionSnapshot } from './types';
import Session from './models/session';

export * from './types';
export * from './models/session';

export class SessionController extends EventTarget {
  private static _instance: SessionController;
  private static _initialized: boolean = false;
  private static configuration: SessionControllerConfiguration = {
    idleTimeBetweenSessions: 300000,
    timeBetweenUpdates: 30000,
    debug: false,
  };

  private static readonly STORAGE_SESSION_VERSION = '1.0.0';
  private static readonly STORAGE_SESSION_VERSION_KEY = 'tm_session_version';
  private static readonly STORAGE_SESSION_KEY = 'tm_session';
  private static readonly STORAGE_SAVED_SNAPSHOT_KEY = 'tm_saved_snapshot';

  private _sessionId: string;
  private _sessionInterval: number | null = null;

  constructor() {
    super();
    if (SessionController._instance) {
      throw new Error('SessionController is a singleton');
    }

    this._sessionId = getUUID();
  }

  static get instance() {
    if (!SessionController._instance) {
      SessionController._instance = new SessionController();
    }
    return SessionController._instance;
  }

  static get initialized() {
    return this._initialized;
  }

  get sessionId() {
    return this._sessionId;
  }

  get storageSession() {
    const storageSessionJson = localStorage.getItem(SessionController.STORAGE_SESSION_KEY);
    if (!storageSessionJson) return null;
    return Session.fromJson(storageSessionJson);
  }

  static create(configuration: SessionControllerConfiguration) {
    this._log('Starting SessionController...');
    if (this.initialized && this.configuration.userId !== configuration.userId) {
      this.delete();
    }
    this._initialized = true;
    this.configuration = configuration;

    window.addEventListener('visibilitychange', this._handleVisibilityChange);

    this._validateStorageSession();

    const instance = this.instance;
    instance._sendEventSessionStart();

    if (document.visibilityState === 'visible') {
      instance._tryReconnectStorageSession();
    }

    instance._setupSessionInterval();
    this._log('SessionController started.');
  }

  static delete() {
    this._log('Deleting Session...');
    if (!SessionController.initialized) return;
    window.removeEventListener('visibilitychange', this._handleVisibilityChange);
    const instance = this.instance;
    instance._sendEventSessionStop();
    instance._stopSessionInterval();
    this._clearStorage();
    this._initialized = false;
    this._log('Session deleted.');
  }

  private static _handleVisibilityChange() {
    switch (document.visibilityState) {
      case 'visible':
        SessionController.instance._handleTabVisible();
        break;
      case 'hidden':
        SessionController.instance._handleTabHidden();
        break;
    }
  }

  private _handleTabVisible() {
    SessionController._log('This tab is now visible, updating session...');
    this._tryReconnectStorageSession();
    this._setupSessionInterval();
  }

  private _handleTabHidden() {
    SessionController._log('This tab is hidden, clearing session interval...');
    this._stopSessionInterval();
  }

  private _createStorageSession() {
    SessionController._log('Creating new session...');
    const session = new Session(this._sessionId, SessionController.configuration.userId);
    localStorage.setItem(SessionController.STORAGE_SESSION_KEY, session.toJson());
    SessionController._log('Session created:');
    SessionController._logSession(session);
    this._sendEventSessionCreate();
  }

  private _updateStorageSession(timestamp = Date.now()) {
    const session = this.storageSession;
    if (!session || (session && !SessionController.validateSession(session))) {
      return this._createStorageSession();
    }
    SessionController._log('Updating session...');
    session.update(timestamp, this._sessionId);
    localStorage.setItem(SessionController.STORAGE_SESSION_KEY, session.toJson());
    SessionController._log('Session updated:');
    SessionController._logSession(session);
    this._sendEventSessionUpdate();
  }

  private _tryReconnectStorageSession(timestamp = Date.now()) {
    const session = this.storageSession;
    if (!session || (session && !SessionController.validateSession(session))) {
      return this._createStorageSession();
    }
    SessionController._log('Trying to reconnect session...');
    session.reconnect(timestamp, this._sessionId);
    localStorage.setItem(SessionController.STORAGE_SESSION_KEY, session.toJson());
    SessionController._log('Session reconnected:');
    SessionController._logSession(session);
    this._sendEventSessionUpdate();
  }

  private _setupSessionInterval() {
    if (document.visibilityState === 'hidden') {
      SessionController._log('This tab is hidden. We will setup the interval when it becomes visible.');
      return;
    }

    SessionController._log('Initializing session interval...');
    this._sessionInterval = window.setInterval(() => {
      this._updateStorageSession();
    }, SessionController.configuration.timeBetweenUpdates);
  }

  private _stopSessionInterval() {
    if (this._sessionInterval) window.clearInterval(this._sessionInterval);
  }

  static validateSession(session: Session): boolean {
    if (!session.id || !session.initialTimeStamp || !session.lastUpdatedTimeStamp || !session.lastUpdatedById) {
      return false;
    }

    if ((SessionController.configuration.userId && session.userId)
      && !SessionController.configuration.userId.startsWith(session.userId)) {
      return false;
    }

    const timeSinceLastUpdate = Date.now() - session.lastUpdatedTimeStamp;
    if (timeSinceLastUpdate > SessionController.configuration.idleTimeBetweenSessions) {
      return false;
    }

    return true;
  }

  _sendEventSessionStart() {
    const session = this.storageSession;
    this._sendSnapshotEvent(SessionEvents.START, session?.snapshot);
  }

  _sendEventSessionStop() {
    const session = this.storageSession;
    this._sendSnapshotEvent(SessionEvents.STOP, session?.snapshot);
  }

  _sendEventSessionCreate() {
    const session = this.storageSession;
    this._sendSnapshotEvent(SessionEvents.CREATE, session?.snapshot);
  }

  _sendEventSessionUpdate() {
    const session = this.storageSession;
    this._sendSnapshotEvent(SessionEvents.UPDATE, session?.snapshot);
  }

  _sendSnapshotEvent(event: string, data?: SessionSnapshot) {
    SessionController._log(`Sending event ${event}.`);
    const eventObject = new CustomEvent<SessionSnapshot>(event, {
      detail: data,
    });

    this.dispatchEvent(eventObject);
  }

  static _log(message: string) {
    if (!SessionController.configuration.debug) return;
    // console.info(`[SessionController] ${message}`);
  }

  static _logSession(session: Session) {
    if (!SessionController.configuration.debug) return;
    // session.print();
  }

  private static _validateStorageSession(): void {
    this._log('Validating storage session...');
    const storageVersion = localStorage.getItem(this.STORAGE_SESSION_VERSION_KEY);
    if (storageVersion === this.STORAGE_SESSION_VERSION) {
      return this._log('Storage session is valid.');
    }

    this._log('Storage session is invalid.');
    localStorage.setItem(this.STORAGE_SESSION_VERSION_KEY, this.STORAGE_SESSION_VERSION);
    this._clearStorage();
  }

  private static _clearStorage() {
    this._log('Clearing storage...');
    localStorage.removeItem(this.STORAGE_SESSION_KEY);
    const keysLength = localStorage.length;
    for (let i = 0; i < keysLength; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_SAVED_SNAPSHOT_KEY)) {
        localStorage.removeItem(key);
      }
    }
    this._log('Storage cleared.');
  }

  static saveSnapshot(key: string, snapshot: SessionSnapshot) {
    localStorage.setItem(`${this.STORAGE_SAVED_SNAPSHOT_KEY}_${key}`, JSON.stringify(snapshot));
  }

  static getSavedSnapshot(key: string): SessionSnapshot | null {
    const snapshotJson = localStorage.getItem(`${this.STORAGE_SAVED_SNAPSHOT_KEY}_${key}`);
    if (!snapshotJson) return null;
    return JSON.parse(snapshotJson);
  }

  static deleteSavedSnapshot(key: string) {
    localStorage.removeItem(`${this.STORAGE_SAVED_SNAPSHOT_KEY}_${key}`);
  }
}
